import { useFetch } from "hooks/api";
import { useMemo, useState, useEffect } from "react";
import { useAppSelector } from "redux/hooks";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import useDSPSellerInfo from "./useDSPSellerInfo";

const formatOptions = (results, formStateIds, selectionMode, labelFormatter, filterList = []) => {
  return results
    ?.filter((result) =>
      selectionMode === "exclude" ? !formStateIds.includes(result.id) : !formStateIds.includes(result.id),
    )
    ?.filter((result) => {
      return !filterList?.includes(result.id);
    })
    .map(labelFormatter);
};

export const useTargetSelector = (
  fetchDataHook,
  formState,
  targetModeAccessor,
  exclusionModeAccessor,
  labelFormatter,
  lineItemId = null,
  additionalParams = null,
) => {
  const [savedSearchResults, setSavedSearchResults] = useState([]);
  const [search, setSearch] = useState("");
  const [selectionMode, setSelectionMode] = useState("target");

  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { dspAdvertiser } = useDSPSellerInfo();

  const selectedTargets = formState[targetModeAccessor];
  const selectedExclusions = formState[exclusionModeAccessor];

  const { data: searchResults, isLoading } = fetchDataHook(search, additionalParams);

  const { data: existingTargetSearchResults } = useFetch(
    ["dspTargets", lineItemId, dspAdvertiser?.id],
    `/dsp/target/`,
    {
      line_item: lineItemId,
      marketplace: marketPlace,
      limit: 5000,
      dsp_seller_info_id: dspAdvertiser?.id,
    },
    {
      enabled: !!lineItemId,
      select: (data) => data?.data?.results,
    },
  );

  useEffect(() => {
    const newResults = existingTargetSearchResults
      ?.map((t) => t.external_details)
      ?.filter((t) => t)
      ?.filter((result) => !savedSearchResults.some((saved) => saved.id === result.id));

    if (existingTargetSearchResults?.length) {
      setSavedSearchResults((prev) => [...prev, ...newResults]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingTargetSearchResults]);

  useEffect(() => {
    const newResults = searchResults?.filter((result) => !savedSearchResults.some((saved) => saved.id === result.id));
    if (newResults?.length) {
      setSavedSearchResults((prev) => [...prev, ...newResults]);
    }
  }, [searchResults, savedSearchResults, selectedTargets, selectedExclusions]);

  const formattedOptions = useMemo(() => {
    return formatOptions(
      searchResults,
      formState[selectionMode === "exclude" ? exclusionModeAccessor : targetModeAccessor] ?? [],
      selectionMode,
      (target) => {
        const { name, id, category } = target;

        return {
          label:
            labelFormatter && typeof labelFormatter === "function" ?
              labelFormatter(target)
            : `${name} (${toTitleCase(snakeCaseToSentenceCase(category))})`,
          value: id,
          ...target,
        };
      },
      selectionMode === "exclude" ? selectedTargets : selectedExclusions,
    );
  }, [
    formState,
    searchResults,
    selectionMode,
    targetModeAccessor,
    exclusionModeAccessor,
    selectedTargets,
    selectedExclusions,
    labelFormatter,
  ]);

  return {
    search,
    setSearch,
    selectionMode,
    setSelectionMode,
    formattedOptions,
    savedSearchResults,
    isLoading,
  };
};
